/**
 * Call join/leave sound preferences in localStorage — no React.
 * `useCallPresenceSoundPreference` subscribes via `useSyncExternalStore`.
 * Independent of the master notification-sound toggle and focus suppression.
 */

import {
  DEFAULT_CALL_PRESENCE_JOIN_SOUND_ID,
  DEFAULT_CALL_PRESENCE_LEAVE_SOUND_ID,
} from '../constants/builtinNotificationSounds';
import type { NotificationSoundId } from '../constants/notificationSoundPreferenceShared';
import {
  formatStoredNotificationGain,
  isValidSoundId,
  parseStoredNotificationGain,
} from './notificationSoundPreferenceStorage';

export type CallPresenceEvent = 'join' | 'leave';

export const CALL_PRESENCE_EVENTS: readonly CallPresenceEvent[] = ['join', 'leave'];

export interface CallPresenceSoundPrefs {
  enabled: boolean;
  /** When false, only other participants' joins/leaves play. */
  includeSelf: boolean;
  soundId: NotificationSoundId;
  customPath: string | null;
  volume: number;
  friendEnabled: boolean;
  friendSoundId: NotificationSoundId;
  friendCustomPath: string | null;
  friendVolume: number;
}

export function defaultCallPresenceSoundPrefs(event: CallPresenceEvent): CallPresenceSoundPrefs {
  const soundId =
    event === 'join' ? DEFAULT_CALL_PRESENCE_JOIN_SOUND_ID : DEFAULT_CALL_PRESENCE_LEAVE_SOUND_ID;
  return {
    enabled: true,
    includeSelf: true,
    soundId,
    customPath: null,
    volume: 1,
    friendEnabled: false,
    friendSoundId: soundId,
    friendCustomPath: null,
    friendVolume: 1,
  };
}

/** Join-event defaults. Leave uses `defaultCallPresenceSoundPrefs('leave')`. */
export const DEFAULT_CALL_PRESENCE_SOUND_PREFS: CallPresenceSoundPrefs = Object.freeze(
  defaultCallPresenceSoundPrefs('join')
);

type Field = keyof CallPresenceSoundPrefs;

const BOOLEAN_FIELDS = ['enabled', 'includeSelf', 'friendEnabled'] as const;
const SOUND_ID_FIELDS = ['soundId', 'friendSoundId'] as const;
const PATH_FIELDS = ['customPath', 'friendCustomPath'] as const;
const VOLUME_FIELDS = ['volume', 'friendVolume'] as const;

const KEY_PREFIX = 'adieuu.app.callPresenceSound.';

export function callPresenceSoundStorageKey(event: CallPresenceEvent, field: Field): string {
  return `${KEY_PREFIX}${event}.${field}`;
}

function read(event: CallPresenceEvent, field: Field): string | null {
  return localStorage.getItem(callPresenceSoundStorageKey(event, field));
}

export function getCallPresenceSoundPrefs(event: CallPresenceEvent): CallPresenceSoundPrefs {
  const prefs = defaultCallPresenceSoundPrefs(event);
  if (typeof localStorage === 'undefined') return prefs;
  try {
    for (const field of BOOLEAN_FIELDS) {
      const v = read(event, field);
      if (v !== null) prefs[field] = v === '1';
    }
    for (const field of SOUND_ID_FIELDS) {
      const v = read(event, field);
      if (isValidSoundId(v)) prefs[field] = v;
    }
    for (const field of PATH_FIELDS) {
      const v = read(event, field);
      prefs[field] = v && v.length > 0 ? v : null;
    }
    for (const field of VOLUME_FIELDS) {
      prefs[field] = parseStoredNotificationGain(read(event, field));
    }
  } catch {
    return defaultCallPresenceSoundPrefs(event);
  }
  return prefs;
}

function serialize(field: Field, value: CallPresenceSoundPrefs[Field]): string | null {
  if (typeof value === 'boolean') return value ? '1' : '0';
  if (typeof value === 'number') return formatStoredNotificationGain(value);
  if (value === null || value === '') return null;
  if ((SOUND_ID_FIELDS as readonly Field[]).includes(field) && !isValidSoundId(value)) {
    return null;
  }
  return value;
}

const listeners = new Set<() => void>();

export function setCallPresenceSoundPref(
  event: CallPresenceEvent,
  patch: Partial<CallPresenceSoundPrefs>
): void {
  if (typeof localStorage === 'undefined') return;
  try {
    for (const [field, value] of Object.entries(patch) as [Field, CallPresenceSoundPrefs[Field]][]) {
      if (value === undefined) continue;
      const key = callPresenceSoundStorageKey(event, field);
      const stored = serialize(field, value);
      if (stored === null) localStorage.removeItem(key);
      else localStorage.setItem(key, stored);
    }
  } catch {
    return;
  }
  for (const l of listeners) l();
}

export function subscribeCallPresenceSoundPreference(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith(KEY_PREFIX)) onStoreChange();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStorage);
  };
}
